import useUnauthenticatedRedirect from 'app/components/auth/useUnauthenticatedRedirect';
import { useHistory } from 'app/components/router/History';
import { ResponseError } from 'app/constants/ResponseErrors';
import { ForbiddenPageRoute, NotFoundPageRoute, MeetingJoinRoute } from 'app/constants/Routes';
import BookingAutoload from 'app/containers/rtc/BookingContext/BookingAutoload';
import BookingPolling from 'app/containers/rtc/BookingContext/BookingPolling';
import { BookingStateProvider, BookingStateConsumer } from 'app/containers/rtc/BookingContext/BookingState';
import {
  BookingId,
  BookingQueryParam,
  BookingType,
  BookingTypeSettings,
  IBookingTuple,
  CallEndReason,
} from 'app/models/rtc/booking/constants';
import { ParticipantType } from 'app/models/rtc/constants';
import { createBooking, fetchBooking } from 'app/models/rtc/remote';
import { History } from 'app/services/history';
import { debug } from 'app/services/logger';
import { EntityStringId } from 'app/types/entity';
import TaskQueue from 'app/utils/queues/TaskQueue';
import React, { Component, createContext, ReactNode, useContext } from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from 'redux';
import FluentdLogger from 'app/services/fluentdLogger';
import { CancelToken } from 'app/utils/cancelToken';
import userAgent, { Browser } from 'app/utils/userAgent';

interface IMapDispatchToProps {
  createBooking: (bookingType: BookingType, cancelToken: CancelToken) => Promise<BookingId>;
  fetchBooking: (bookingType: BookingType, bookingId: BookingId, cancelToken: CancelToken) => Promise<BookingId>;
}

export interface IBookingInterface {
  changeParticipantType: (participantType: ParticipantType) => void;
  clearActiveBooking: (endReason?: CallEndReason) => void;
  clearBookings: () => void;
  createBooking: (bookingType: BookingType) => Promise<EntityStringId>;
  fetchBooking: (bookingType: BookingType, bookingId: BookingId, hostKey?: string) => Promise<void>;
  fetchLastBooking: () => Promise<void>;
  handleBookingError: (error: Error) => void;
  refreshBooking: () => Promise<void>;
  setBookingState: (bookingType: BookingType, bookingId: BookingId) => void;
}

const noProviderError = () => {
  throw new Error('Do not use the BookingInterfaceConsumer without the provider');
};

// The default booking interface which errors if it is used. This should be replaced
// by the actually booking interface. Having the interface default to undefined lead
// to reimplementing this error anywhere the interface was used.
const undefinedBookingInterface: IBookingInterface = {
  changeParticipantType: noProviderError,
  clearActiveBooking: noProviderError,
  clearBookings: noProviderError,
  createBooking: noProviderError,
  fetchBooking: noProviderError,
  fetchLastBooking: noProviderError,
  handleBookingError: noProviderError,
  refreshBooking: noProviderError,
  setBookingState: noProviderError,
};

const BookingInterfaceContext = createContext<IBookingInterface>(undefinedBookingInterface);
interface IProps extends IMapDispatchToProps {
  children: ReactNode;
  history: History;
  unauthenticatedRedirect: () => void;
}

interface IState {
  activeBooking?: IBookingTuple & { participantType: ParticipantType; };
  previousBooking?: IBookingTuple;
}

export const BookingInterfaceProvider = BookingInterfaceContext.Provider;
export const BookingInterfaceConsumer = BookingInterfaceContext.Consumer;

export const useBookingInterface = () => useContext(BookingInterfaceContext);

class BookingProvider extends Component<IProps, IState> {
  private readonly queue = new TaskQueue();
  private readonly interface: IBookingInterface;
  private unregisterPreservingBookingParams?: () => void;

  public constructor(props: IProps) {
    super(props);

    this.interface = {
      changeParticipantType:     this.changeParticipantType,
      clearActiveBooking:        this.clearActiveBooking,
      clearBookings:             this.clearBookings,
      createBooking:             this.createBooking,
      fetchBooking:              this.fetchBooking,
      fetchLastBooking:          this.fetchLastBooking,
      handleBookingError:        this.handleBookingError,
      refreshBooking:            this.refreshBooking,
      setBookingState:           this.setBookingState,
    };

    this.state = {};
  }

  public componentDidUpdate() {
    // The user is actively on a booking, preserve any booking parameters once they are set.
    if (!this.unregisterPreservingBookingParams && this.state.activeBooking) {
      const { bookingType } = this.state.activeBooking;
      if (!BookingTypeSettings[bookingType].controlsConnection) {
        this.unregisterPreservingBookingParams = this.props.history.preserveParams(
          BookingQueryParam.BookingId,
          BookingQueryParam.BookingType,
        );
      }
    }
  }

  public componentWillUnmount() {
    const { activeBooking } = this.state;

    if (this.unregisterPreservingBookingParams && activeBooking) {
      const { bookingType, bookingId } = activeBooking;
      const { getPostCallLocation } = BookingTypeSettings[bookingType];
      this.unregisterPreservingBookingParams();
      if (getPostCallLocation) {
        this.props.history.push(
          getPostCallLocation(bookingId, CallEndReason.Left),
        );
      }
    }

    this.queue.abort();
  }

  public render() {
    return (
      <BookingInterfaceProvider value={this.interface}>
        <BookingStateProvider {...this.state.activeBooking}>
          <BookingStateConsumer>{(bookingState) => (
            <BookingAutoload activeBooking={this.state.activeBooking} bookingInterface={this.interface}>
              <BookingPolling bookingState={bookingState} fetch={this.refreshBooking}>
                {this.props.children}
              </BookingPolling>
            </BookingAutoload>
          )}</BookingStateConsumer>
        </BookingStateProvider>
      </BookingInterfaceProvider>
    );
  }

  private changeParticipantType = (participantType: ParticipantType) => {
    this.setState((prevState) => {
      if (!prevState.activeBooking) {
        throw new Error('Cannot change participant type when no booking is present.');
      }

      return { activeBooking: { ...prevState.activeBooking, participantType } };
    });
  };

  private clearActiveBooking = (endReason: CallEndReason = CallEndReason.Left) => {
    debug('Clearing active booking.');
    const { activeBooking } = this.state;

    FluentdLogger.setContext('booking_type', undefined);
    FluentdLogger.setContext('booking_id', undefined);

    if (this.unregisterPreservingBookingParams && activeBooking) {
      const { bookingType, bookingId } = activeBooking;
      const { getPostCallLocation } = BookingTypeSettings[bookingType];
      this.unregisterPreservingBookingParams();
      this.unregisterPreservingBookingParams = undefined;
      if (getPostCallLocation) {
        this.props.history.push(getPostCallLocation(bookingId, endReason));
      }
    }
    this.setState(() => ({ activeBooking: undefined }));
  };

  /**
   * Clears both the active booking and previous booking.
   */
  private clearBookings = () => {
    this.setState(() => ({ activeBooking: undefined, previousBooking: undefined }));
  };

  private createBooking = async (bookingType: BookingType): Promise<BookingId> => {
    this.queue.abort();

    return await this.queue.push(async (cancelToken) => {
      const bookingId = await this.props.createBooking(bookingType, cancelToken);

      this.setBookingState(bookingType, bookingId);

      return bookingId;
    });
  };

  /**
   * Updates booking information without setting anything about participant.
   */
  private refreshBooking = async () => {
    const { activeBooking } = this.state;

    if (activeBooking) {
      const { bookingType, bookingId } = activeBooking;
      await this.fetchBooking(bookingType, bookingId);
    }
  };

  private fetchBooking = async (bookingType: BookingType, bookingId: BookingId, hostKey?: string): Promise<void> => {
    if (this.requiresIntermediaryPage(bookingType)) {
      this.props.history.replace(MeetingJoinRoute.getPathname({ id: bookingId }));
      return;
    }

    this.queue.abort();

    await this.queue.push(async (cancelToken) => {
      try {
        // Use the ID on the entity for setting the booking. This is necessary
        // for CombinedBookingLesson to work, which may assign the ID of the
        // first lesson in the group.
        const returnedBookingId = await this.props.fetchBooking(bookingType, bookingId, cancelToken);

        this.setBookingState(bookingType, returnedBookingId, hostKey);

        FluentdLogger.setContext('booking_type', bookingType);
        FluentdLogger.setContext('booking_id', returnedBookingId);
      } catch (error) {
        if (error !== cancelToken.getReason()) {
          this.handleBookingError(error);
          // Force a reject since bookingID cannot be obtained.
          throw error;
        }
      }
    });
  };

  private fetchLastBooking = async () => {
    const { previousBooking } = this.state;

    if (!previousBooking) {
      throw new Error('Trying to refetch a lesson without any booking state.');
    }

    const { bookingType, bookingId } = previousBooking;
    await this.fetchBooking(bookingType, bookingId);
  };

  private requiresIntermediaryPage = (bookingType: BookingType) => {
    return userAgent().browser === Browser.FBMessengerAndroid && bookingType === BookingType.Meeting;
  };

  /**
   * This should be used for handling 401, 403, and 404 responses from the API for fetching the booking data.
   */
  private handleBookingError = (error: Error) => {
    const handlers = [
      { status: 401, handler: () => this.props.unauthenticatedRedirect() },
      { status: 403, handler: () => this.props.history.replace(ForbiddenPageRoute.getPathname()) },
      { status: 404, handler: () => this.props.history.replace(NotFoundPageRoute.getPathname()) },
    ];

    const result = handlers.find(({ status }) => {
      if (error instanceof ResponseError && error.status === status) {
        return true;
      }

      return false;
    });

    if (result) {
      this.clearActiveBooking();
      result.handler();
    } else {
      throw error;
    }
  };

  // TODO: Don't expose this. Make it easier to test state by separating the logic.
  private setBookingState = (bookingType: BookingType, bookingId: BookingId, hostKey?: string): void => {
    debug('Setting active booking.', { bookingId, bookingType });

    this.setState((prevState) => ({
      activeBooking: {
        bookingId,
        bookingType,
        hostKey,
        participantType: BookingTypeSettings[bookingType].defaultParticipantType,
      },
      previousBooking: prevState.activeBooking,
    }));
  };
}

const mapDispatchToProps = (dispatch: AppDispatch): IMapDispatchToProps => ({
  createBooking: (bookingType, cancelToken) => dispatch(createBooking(bookingType, cancelToken)),
  fetchBooking: (bookingType, bookingId, cancelToken) => dispatch(fetchBooking(bookingType, bookingId, cancelToken)),
});

export default connect(null, mapDispatchToProps)(
  (props: Pick<IProps, Exclude<keyof IProps, 'unauthenticatedRedirect' | 'history'>>) => {
    const history = useHistory();
    const unauthenticatedRedirect = useUnauthenticatedRedirect();

    return (
      <BookingProvider unauthenticatedRedirect={unauthenticatedRedirect} history={history} {...props} />
    );
  },
);
